import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Button, Form, Card, Badge, Spinner, Alert } from "react-bootstrap";
import { FloatCardComponent } from "../../components/cards";
import GastosTableComponent from "../../components/tables/GastosTableComponent";
import { ButtonComponent } from "../../components/elements";
import PageLayout from "../../layouts/PageLayout";
import UploadGastosExcel from "../../components/UploadGastosExcel";
import { getFondos, getGastos, getDashboardGastos } from "../../api/gastos.api.ts";

export default function GastosListPage() {

    const { t } = useContext(TranslatorContext)
    
    // Estado para el año seleccionado
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Estados para datos de la API
    const [fondos, setFondos] = useState([]);
    const [gastos, setGastos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    
    // Estado para los datos reales del dashboard
    const [estadisticas, setEstadisticas] = useState({
        presupuesto_total: 0,
        total_ingresos: 0,
        total_gastos_monto: 0,
        fondos_activos: 0,
        saldo_disponible: 0,
        porcentaje_ejecucion: 0
    });

    // Cargar datos desde la API
    const cargarDatos = async (anio) => {
        setLoading(true);
        setError(null);
        try {
            const [fondosResponse, gastosResponse, dashboardResponse] = await Promise.all([
                getFondos(anio),
                getGastos({ anio, limite: 1000000 }), // Últimos 10 gastos
                getDashboardGastos(anio)
            ]);

            if (fondosResponse.success) {
                setFondos(fondosResponse.data);
            }

            if (gastosResponse.success) {
                setGastos(gastosResponse.data);
            }

            if (dashboardResponse.success && dashboardResponse.data.estadisticas) {
                // Actualizar estadísticas con datos reales de la API
                // - presupuesto_total: Suma de presupuestos anuales de fondos activos
                // - total_ingresos: Cuotas pagadas de giras del año seleccionado
                // - total_gastos_monto: Suma de todos los gastos del año
                // - saldo_disponible: total_ingresos - total_gastos_monto
                // - porcentaje_ejecucion: (total_gastos / presupuesto_total) * 100
                setEstadisticas({
                    presupuesto_total: dashboardResponse.data.estadisticas.presupuesto_total || 0,
                    total_ingresos: dashboardResponse.data.estadisticas.total_ingresos || 0,
                    total_gastos_monto: dashboardResponse.data.estadisticas.total_gastos_monto || 0,
                    fondos_activos: dashboardResponse.data.estadisticas.fondos_activos || 0,
                    saldo_disponible: dashboardResponse.data.estadisticas.saldo_disponible || 0,
                    porcentaje_ejecucion: dashboardResponse.data.estadisticas.porcentaje_ejecucion || 0
                });
            }
        } catch (error) {
            setError('Error al cargar los datos');
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    // Función para recargar solo los gastos
    const recargarGastos = async () => {
        try {
            const gastosResponse = await getGastos({ anio: selectedYear, limite: 10 });
            if (gastosResponse.success) {
                setGastos(gastosResponse.data);
            }
        } catch (error) {
            console.error('Error al recargar gastos:', error);
        }
    };

    // Cargar datos al cambiar el año o después de importar
    useEffect(() => {
        cargarDatos(selectedYear);
    }, [selectedYear, refreshTrigger]);

    // Callback cuando se importan gastos exitosamente
    const handleImportSuccess = (resultado) => {
        setRefreshTrigger(prev => prev + 1);
    };

    // Calcular datos dinámicos basados en las estadísticas actuales
    const saldoDisponible = estadisticas.saldo_disponible;
    const porcentajeEjecucion = estadisticas.porcentaje_ejecucion;

    // Generar años disponibles (memoizado para evitar recálculos)
    const aniosDisponibles = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            years.push(i);
        }
        return years;
    }, []);

    // Memoizar los cards de resumen para evitar recálculos en cada render
    const floatsCards = useMemo(() => [
        { 
            "title": "presupuesto anual", 
            "digit": parseFloat(estadisticas.presupuesto_total) || 0, 
            "icon": "account_balance", 
            "variant": "lg blue" 
        }, 
        { 
            "title": "total ingresos", 
            "digit": parseFloat(estadisticas.total_ingresos) || 0, 
            "icon": "trending_up", 
            "variant": "lg green" 
        }, 
        { 
            "title": "total gastos", 
            "digit": parseFloat(estadisticas.total_gastos_monto) || 0, 
            "icon": "trending_down", 
            "variant": "lg red" 
        },
        { 
            "title": "saldo disponible", 
            "digit": parseFloat(saldoDisponible) || 0, 
            "icon": "account_balance_wallet", 
            "variant": saldoDisponible > 0 ? "lg green" : "lg orange" 
        }
    ], [estadisticas, saldoDisponible]);

    // Formatear moneda (función memoizada)
    const formatCurrency = useMemo(() => {
        return (amount) => {
            const value = typeof amount === 'number' ? amount : 0;
            return new Intl.NumberFormat('es-CL', {
                style: 'currency',
                currency: 'CLP',
                minimumFractionDigits: 0
            }).format(value);
        };
    }, []);

    const formatNumber = (number) => {
        const value = typeof number === 'number' ? number : 0;
        return value;
    };

    // Función para obtener color por porcentaje (memoizada)
    const getColorByPercentage = useCallback((percentage) => {
        if (percentage > 70) return 'success';
        if (percentage > 40) return 'warning';
        return 'danger';
    }, []);

    // Memoizar los fondos procesados para evitar recálculos en cada render
    const fondosProcesados = useMemo(() => {
        return fondos.slice(0, 4).map((fondo) => {
            const presupuesto = parseFloat(fondo.fon_presupuesto_anual) || 0;
            const gastado = 0; // Se calculará desde gastos reales
            const disponible = presupuesto - gastado;
            const porcentajeDisponible = presupuesto > 0 ? (disponible / presupuesto) * 100 : 0;
            const bgColor = getColorByPercentage(porcentajeDisponible);

            return {
                ...fondo,
                presupuesto,
                gastado,
                disponible,
                porcentajeDisponible,
                bgColor
            };
        });
    }, [fondos, getColorByPercentage]);

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Gestión de Gastos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">Administración</Link></li>
                                <li className="mc-breadcrumb-item">Gastos</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Selector de Año y Controles */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Año de Gestión</Form.Label>
                                        <Form.Select 
                                            value={selectedYear} 
                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            className="form-select-lg"
                                        >
                                            {aniosDisponibles.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <h6 className="text-muted mb-1">Presupuesto Anual</h6>
                                        <h4 className="text-primary mb-0">{formatCurrency(parseFloat(estadisticas.presupuesto_total) || 0)}</h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <h6 className="text-muted mb-1">% Ejecución</h6>
                                        <h4 className={`mb-0 ${parseFloat(porcentajeEjecucion) > 80 ? 'text-danger' : parseFloat(porcentajeEjecucion) > 60 ? 'text-warning' : 'text-success'}`}>
                                            {formatNumber(parseFloat(porcentajeEjecucion) || 0).toFixed(1)}%
                                        </h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <h6 className="text-muted mb-1">Fondos Activos</h6>
                                        <h4 className="text-info mb-0">{formatNumber(parseFloat(estadisticas.fondos_activos) || 0)}</h4>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                
                {/* Cards de resumen */}
                {floatsCards.map((float, index) => (
                    <Col key={ index } sm={6} lg={3}>
                        <FloatCardComponent 
                            variant={ float.variant }
                            digit={formatCurrency(float.digit)}
                            title={t(float.title)}
                            icon={ float.icon }
                        />
                    </Col>
                ))}

                {/* Botones de acciones principales */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-card-body">
                            <Row>
                                <Col md={2}>
                                    <Link to="/gastos/registro" className="text-decoration-none">
                                        <Button variant="primary" className="w-100 mb-2">
                                            <i className="material-icons me-2">add</i>
                                            Registrar Gasto
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Link to="/gastos/fondos" className="text-decoration-none">
                                        <Button variant="info" className="w-100 mb-2">
                                            <i className="material-icons me-2">account_balance</i>
                                            Gestión Fondos
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Link to="/gastos/reportes" className="text-decoration-none">
                                        <Button variant="success" className="w-100 mb-2">
                                            <i className="material-icons me-2">assessment</i>
                                            Reportes
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Link to="/gastos/configuracion" className="text-decoration-none">
                                        <Button variant="secondary" className="w-100 mb-2">
                                            <i className="material-icons me-2">settings</i>
                                            Configuración
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Button variant="warning" className="w-100 mb-2">
                                        <i className="material-icons me-2">backup</i>
                                        Exportar {selectedYear}
                                    </Button>
                                </Col>
                                <Col md={2}>
                                    <Button variant="outline-primary" className="w-100 mb-2">
                                        <i className="material-icons me-2">upload_file</i>
                                        Importar Datos
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Importar Gastos desde Excel */}
                <Col xl={12}>
                    <UploadGastosExcel 
                        anio={selectedYear}
                        onImportSuccess={handleImportSuccess}
                    />
                </Col>

                {/* Estado de Fondos Rápido */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Estado de Fondos - {selectedYear}</h5>
                        </Card.Header>
                        <Card.Body>
                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    <i className="material-icons me-2">error</i>
                                    {error}
                                </Alert>
                            )}

                            {loading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Cargando fondos...</span>
                                    </Spinner>
                                    <p className="mt-2">Cargando estado de fondos...</p>
                                </div>
                            ) : (
                                <Row>
                                    {fondos.length === 0 ? (
                                        <Col md={12}>
                                            <div className="text-center py-4">
                                                <i className="material-icons text-muted" style={{fontSize: '48px'}}>folder_open</i>
                                                <h6 className="text-muted mt-2">No hay fondos configurados para {selectedYear}</h6>
                                                <Link to="/gastos/fondos">
                                                    <Button variant="primary" size="sm">
                                                        <i className="material-icons me-1">add</i>
                                                        Configurar Fondos
                                                    </Button>
                                                </Link>
                                            </div>
                                        </Col>
                                    ) : (
                                        fondosProcesados.map((fondo) => (
                                            <Col key={fondo.fon_id} md={3}>
                                                <div className="d-flex align-items-center p-3 border rounded">
                                                    <div className="me-3">
                                                        <div className={`bg-${fondo.bgColor} rounded-circle d-flex align-items-center justify-content-center`} style={{width: '40px', height: '40px'}}>
                                                            <i className="material-icons text-white">account_balance</i>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1">{fondo.fon_nombre}</h6>
                                                        <small className={`text-${fondo.bgColor}`}>
                                                            {fondo.porcentajeDisponible.toFixed(0)}% disponible
                                                        </small>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))
                                    )}
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Tabla de gastos recientes */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header">
                            <h4 className="mc-card-title">Gastos Recientes - {selectedYear}</h4>
                            <div className="mc-card-header-option">
                                <Badge bg="primary" className="me-2">
                                    {gastos.length} registros
                                </Badge>
                                
                            </div>
                        </div>
                        <Row>
                            <Col xl={12}>
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Cargando gastos...</span>
                                        </Spinner>
                                        <p className="mt-2">Cargando gastos recientes...</p>
                                    </div>
                                ) : gastos.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="material-icons text-muted" style={{fontSize: '48px'}}>receipt_long</i>
                                        <h6 className="text-muted mt-2">No hay gastos registrados para {selectedYear}</h6>
                                        <Link to="/gastos/registro">
                                            <Button variant="primary" size="sm">
                                                <i className="material-icons me-1">add</i>
                                                Registrar Primer Gasto
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    
                                    <GastosTableComponent 
                                        thead={[
                                            { head: "Código" },
                                            { head: "Descripción" },
                                            { head: "Categoría" },
                                            { head: "Monto" },
                                            { head: "Fecha" },
                                            { head: "Estado" },
                                            { head: "Acción" }
                                        ]} 
                                        tbody={gastos.map(gasto => ({
                                            gas_id: gasto.gas_id,
                                            gas_codigo: gasto.gas_codigo,
                                            gas_descripcion: gasto.gas_descripcion,
                                            gas_observaciones: gasto.gas_observaciones,
                                            categoria_nombre: gasto.categoria_nombre || 'Sin categoría',
                                            categoria_color: gasto.categoria_color,
                                            gas_monto: gasto.gas_monto,
                                            gas_fecha_gasto: gasto.gas_fecha_gasto,
                                            gas_estado: gasto.gas_estado,
                                            action: {
                                                view: 'visibility',
                                                edit: 'edit',
                                                delete: 'delete'
                                            }
                                        }))}
                                        onGastoEliminado={recargarGastos}
                                    />
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}
